import { CONSUMER_POLICY_REVIEW_PRICES_IN_CENTS } from '@shared/consumerPlan';
import { ChoiceList } from '../components/choice-list';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { money } from '../lib/format';
import { usePolicyReviewDraft, usePolicyReviewDraftActions } from '../stores/policy-review-draft-store';
import type { ConsumerStepProps } from './step-props';

function price(type: ConsumerPolicyReviewType): string {
  return money(CONSUMER_POLICY_REVIEW_PRICES_IN_CENTS[type] / 100);
}

function fee(type: ConsumerPolicyReviewType) {
  return (
    <span className="block text-gray-950">
      <span className="font-semibold">{price(type)}</span>
      <span className="text-muted-foreground font-normal"> one-time fee</span>
    </span>
  );
}

export function ExistingPolicyStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { reviewType } = usePolicyReviewDraft();
  const { setReviewType } = usePolicyReviewDraftActions();

  return (
    <StepFrame
      step="existing-policy"
      title="What would you like us to review?"
      subtitle="Upload your documents. We’ll show how your policy fits your care scenario."
      onBack={() => back('options')}
      help={
        <>
          <p>The {price('policy')} review maps your current policy to the care scenario you’ve chosen. The {price('rate')} review also compares the options in a rate-increase or reduced-benefit notice.</p>
          <p>Each includes projected policy payments, your expected out-of-pocket care cost, and a PDF. The {price('rate')} is the total fee.</p>
          <p>We aim for 24–48 hours after receiving complete documents. Some reviews may take up to 7 business days.</p>
        </>
      }
      primary={{ label: 'Continue', onClick: () => goTo('review-upload'), disabled: !reviewType }}
      next="upload the documents for your review."
    >
      <ChoiceList
        label="Review type"
        value={reviewType}
        onChange={setReviewType}
        choices={[
          {
            value: 'policy',
            title: 'Understand my current policy',
            description: (
              <>
                {fee('policy')}
                Policy analysis, updated care plan, and PDF.
              </>
            ),
          },
          {
            value: 'rate',
            title: 'Compare a rate increase or reduced benefits',
            description: (
              <>
                {fee('rate')}
                Analysis of each offered option, updated care plan, and PDF.
              </>
            ),
          },
        ]}
      />
    </StepFrame>
  );
}
